import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Dropdown, Tab, Nav, Spinner, Badge, Alert, Button } from 'react-bootstrap';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { formatSSNForDisplay } from '../../../../utils/ssnUtils';
import PageTitle from '../../../layouts/PageTitle';
import { useTable, useFilters, useGlobalFilter, usePagination, useSortBy } from 'react-table';
import { GlobalFilter } from './GlobalFilter';
import { useHistory } from 'react-router-dom';
import { NoData } from '../../SharedComponents/NoData';
import Swal from 'sweetalert2';
import EmployeeEditModal from './EmployeeEditModal';
import * as payrollApi from '../../../../services/api/payrollApi';

// Lucide React Icons
import {
  Users,
  UserX,
  Plus,
  FileDown,
  RefreshCw,
  Loader2,
  Eye,
  Edit3,
  Archive,
  RotateCcw,
  Mail,
  Search,
  X,
  Umbrella
} from 'lucide-react';

const EmployeeList = () => {
  const { companyId } = useParams();
  const [employees, setEmployees] = useState({ activeEmployees: [], inactiveEmployees: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  // employee_id → { remainingPaidDays, advanceDaysTaken, ... } — fetched
  // in one bulk call after the employee list loads; failures swallowed
  // so the list renders regardless.
  const [vacationSnapshots, setVacationSnapshots] = useState({});

  const history = useHistory();

  const fetchEmployees = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError(null);

    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/hr/employees`), {
        params: {
          page,
          pageSize,
        },
        withCredentials: true
      });

      const active = response.data.data?.activeEmployees || [];
      const inactive = response.data.data?.inactiveEmployees || [];
      setEmployees({ activeEmployees: active, inactiveEmployees: inactive });
      setTotalEmployees(response.data.data?.total || 0);
      setTotalPages(response.data.data?.totalPages || Math.ceil((response.data.data?.total || 0) / pageSize));
      setError(null);

      // Pull vacation snapshots in one bulk call. Non-blocking: if it fails
      // or the backend hasn't been migrated yet, we still render the list.
      payrollApi.getVacationBalancesBulk(companyId)
        .then((r) => {
          const byId = {};
          for (const s of (r.data?.snapshots || [])) byId[s.employeeId] = s;
          setVacationSnapshots(byId);
        })
        .catch(() => { /* swallow — list is the priority */ });
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Ett fel uppstod vid hämtning av anställda';
      setError(errorMessage);
      console.error('Error fetching employees:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchEmployees(true);
  };

  useEffect(() => {
    fetchEmployees();
  }, [page, pageSize]);

  const handleExportData = async (format = 'csv') => {
    setExportLoading(true);
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/hr/employees/export`),
        {
          params: { format },
          responseType: 'blob',
          withCredentials: true
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `anstallda-${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        icon: 'success',
        title: 'Export klar!',
        text: 'Anställdalistan har exporterats',
        timer: 1500
      });
    } catch (error) {
      console.error('Export error:', error);
      let errorMessage = 'Ett fel uppstod vid export av anställdalistan';

      if (error.response && error.response.data) {
        try {
          const blob = error.response.data;
          const text = await blob.text();
          const data = JSON.parse(text);
          if (data && data.message) {
            errorMessage = data.message;
          }
        } catch (e) {
          // Fallback to default error message
        }
      }

      Swal.fire({
        icon: 'error',
        title: 'Export misslyckades',
        text: errorMessage
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handleArchiveAction = async (employeeId, action) => {
    const isArchiving = action === 'archive';
    const result = await Swal.fire({
      title: isArchiving ? 'Arkivera anställd?' : 'Återställ anställd?',
      text: isArchiving
        ? 'Är du säker på att du vill arkivera denna anställd?'
        : 'Är du säker på att du vill återställa denna anställd?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: isArchiving ? 'Ja, arkivera' : 'Ja, återställ',
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      setActionInProgress(true);
      try {
        const apiUrl = getApiUrl(`${companyId}/hr/employees/${employeeId}`);
        const newStatus = isArchiving ? 'archived' : 'active';
        await axios.patch(apiUrl, { status: newStatus }, { withCredentials: true });
        await fetchEmployees();
        Swal.fire({
          icon: 'success',
          title: 'Klart!',
          text: isArchiving
            ? 'Anställd har arkiverats'
            : 'Anställd har återställts',
          timer: 1500
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Fel',
          text: 'Ett fel uppstod vid uppdatering av anställd status'
        });
      } finally {
        setActionInProgress(false);
      }
    }
  };

  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setSelectedEmployee(null);
  };

  const handleEmployeeUpdate = (updatedEmployee) => {
    setEmployees(prev => {
      const newActiveEmployees = prev.activeEmployees.map(emp =>
        emp.employee_id === updatedEmployee.employee_id ? updatedEmployee : emp
      );
      const newInactiveEmployees = prev.inactiveEmployees.map(emp =>
        emp.employee_id === updatedEmployee.employee_id ? updatedEmployee : emp
      );

      return {
        activeEmployees: newActiveEmployees,
        inactiveEmployees: newInactiveEmployees
      };
    });

    Swal.fire({
      icon: 'success',
      title: 'Sparat!',
      text: 'Ändringar har sparats.',
      timer: 1500
    });
  };

  const tabData = useMemo(() => [
    {
      name: "Aktiva",
      data: employees.activeEmployees,
      icon: Users,
      archiveAction: (employeeId) => handleArchiveAction(employeeId, 'archive'),
      actionIcon: Archive,
      badge: employees.activeEmployees?.length || 0,
      editAction: handleEditEmployee
    },
    {
      name: "Arkiverade",
      data: employees.inactiveEmployees,
      icon: UserX,
      archiveAction: (employeeId) => handleArchiveAction(employeeId, 'unarchive'),
      actionIcon: RotateCcw,
      badge: employees.inactiveEmployees?.length || 0,
      editAction: handleEditEmployee
    }
  ], [employees]);

  if (loading && !employees.activeEmployees.length && !employees.inactiveEmployees.length) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
        
        .table td {
          vertical-align: middle !important;
        }
        
        .clickable-row {
          cursor: pointer;
        }
        
        .clickable-row:hover {
          background-color: #f8f9fa;
        }
        
        /* Enhanced tab styling with active state indicators */
        .nav-tabs {
          border-bottom: 2px solid #e2e8f0;
          background: #f8fafc;
          padding: 0 1rem;
          margin: 0;
        }
        
        .nav-tabs .nav-item {
          margin-bottom: 0;
        }
        
        .nav-tabs .nav-link {
          border: none;
          border-radius: 0;
          background: transparent;
          color: var(--bs-gray);
          font-weight: 500;
          padding: 1rem 1.5rem;
          margin: 0;
          transition: all 0.3s ease;
          position: relative;
          border-bottom: 3px solid transparent;
        }
        
        .nav-tabs .nav-link:hover {
          background-color: rgba(59, 130, 246, 0.05);
          color: var(--bs-primary);
          border-color: transparent;
        }
        
        .nav-tabs .nav-link.active {
          background-color: transparent;
          color: var(--bs-primary);
          border-color: transparent;
          border-bottom: 3px solid var(--bs-primary);
          font-weight: 600;
        }
        
        .nav-tabs .nav-link.active:hover {
          background-color: rgba(59, 130, 246, 0.05);
        }
        
        /* Badge styling within tabs */
        .nav-tabs .nav-link .badge {
          background-color: #e2e8f0 !important;
          color: var(--bs-gray) !important;
          font-size: 0.75rem;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        
        .nav-tabs .nav-link:hover .badge {
          background-color: rgba(59, 130, 246, 0.1) !important;
          color: var(--bs-primary) !important;
        }
        
        .nav-tabs .nav-link.active .badge {
          background-color: var(--bs-primary) !important;
          color: white !important;
        }
        
        /* Tab content area */
        .tab-content {
          background: white;
          border-radius: 0 0 12px 12px;
        }
        
        .search-input-wrapper {
          position: relative;
        }
        
        .search-clear-btn {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
        }
        
        .search-clear-btn:hover {
          background-color: #f3f4f6;
          color: #374151;
        }
      `}</style>

      <PageTitle activeMenu="Anställda" motherMenu="Personal" />

      <div className="col-12">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {employees.activeEmployees.length === 0 && employees.inactiveEmployees.length === 0 ? (
          <>
            <NoData message="Det finns inga anställda att visa. Skapa en för att kunna se dem här." />
            <div className="d-flex justify-content-center p-4">
              <Button
                variant="primary"
                as={Link}
                to={`/${companyId}/create-employee`}
              >
                <Plus size={16} className="me-2" />
                Skapa din första anställd
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Enhanced header with actions */}
            <div className="d-flex align-items-center justify-content-end mb-4">

              <div className="d-flex gap-2">
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    disabled={exportLoading}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    {exportLoading ? (
                      <>
                        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        Exporterar...
                      </>
                    ) : (
                      <>
                        <FileDown size={16} />
                        Exportera
                      </>
                    )}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => handleExportData('csv')}>
                      <FileDown size={14} className="me-2" />
                      CSV (.csv)
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>

                <Button
                  variant="primary"
                  as={Link}
                  to={`/${companyId}/create-employee`}
                  disabled={actionInProgress}
                >
                  <Plus size={16} className="me-2" />
                  Ny anställd
                </Button>
              </div>
            </div>

            {refreshing && (
              <div className="text-center mb-3">
                <Loader2 size={16} className="me-1" style={{ animation: 'spin 1s linear infinite' }} />
                Uppdaterar anställdlista...
              </div>
            )}

            {/* Enhanced card with tabs */}
            <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <div className="card-body p-0">
                <div className="custom-tab-1">
                  <Tab.Container defaultActiveKey={tabData[0].name.toLowerCase()}>
                    <Nav as="ul" className="nav-tabs px-4 pt-4 mb-0" style={{ borderBottom: '1px solid #e2e8f0' }}>
                      {tabData.map((data, i) => (
                        <Nav.Item as="li" key={i}>
                          <Nav.Link eventKey={data.name.toLowerCase()} className="d-flex align-items-center px-4 py-3">
                            <data.icon size={18} className="me-2" />
                            {data.name}
                            <Badge bg="primary" className="ms-2">
                              {data.badge}
                            </Badge>
                          </Nav.Link>
                        </Nav.Item>
                      ))}
                    </Nav>
                    <Tab.Content>
                      {loading && (
                        <div className="text-center py-4">
                          <Spinner animation="border" size="sm" />
                          <span className="ms-2">Uppdaterar...</span>
                        </div>
                      )}
                      {!loading && tabData.map((data, i) => (
                        <Tab.Pane eventKey={data.name.toLowerCase()} key={i}>
                          <EmployeeTable
                            employees={data.data}
                            archiveAction={data.archiveAction}
                            actionIcon={data.actionIcon}
                            editAction={data.editAction}
                            page={page}
                            pageSize={pageSize}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            actionInProgress={actionInProgress}
                            vacationSnapshots={vacationSnapshots}
                          />
                        </Tab.Pane>
                      ))}
                    </Tab.Content>
                  </Tab.Container>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Employee Edit Modal */}
        {selectedEmployee && (
          <EmployeeEditModal
            show={showEditModal}
            handleClose={handleEditModalClose}
            employee={selectedEmployee}
            companyId={companyId}
            onSave={handleEmployeeUpdate}
          />
        )}
      </div>
    </>
  );
};

const EmployeeTable = ({
  employees,
  archiveAction,
  actionIcon,
  editAction,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  actionInProgress,
  vacationSnapshots = {}
}) => {
  const { companyId } = useParams();
  const history = useHistory();
  const [localFilter, setLocalFilter] = useState('');

  const EMPLOYEES_COLUMNS = useMemo(() => [
    {
      Header: 'Id',
      accessor: 'employee_id',
      show: false,
      hidden: true
    },
    {
      Header: 'Anställd',
      accessor: 'name',
      sortType: 'alphanumeric',
      Cell: ({ value, row }) => (
        <div className="d-flex align-items-center">
          <div
            className="me-3 d-flex align-items-center justify-content-center"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: 'var(--rgba-primary-1)',
              color: 'var(--bs-primary)'
            }}
          >
            <Users size={18} />
          </div>
          <div>
            <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1f2937' }}>
              {value || '-'}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {formatSSNForDisplay(row.original.ssn) || '-'}
            </div>
          </div>
        </div>
      )
    },
    {
      Header: 'E-post',
      accessor: 'email',
      sortType: 'alphanumeric',
      Cell: ({ value }) => (
        <div className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
          <Mail size={14} className="me-2 text-muted" />
          <a
            href={`mailto:${value}`}
            className="text-primary text-decoration-none"
            style={{ fontSize: '0.875rem' }}
          >
            {value}
          </a>
        </div>
      )
    },
    {
      Header: 'Semester',
      accessor: 'vacation_balance',
      disableSortBy: true,
      Cell: ({ row }) => {
        const snap = vacationSnapshots[row.original.employee_id];
        if (!snap) {
          return <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>–</span>;
        }
        const remaining = snap.remainingPaidDays ?? 0;
        const advance = snap.advanceDaysTaken ?? 0;
        const hasAdvance = advance > 0;
        return (
          <div
            className="d-flex align-items-center"
            onClick={(e) => e.stopPropagation()}
            title={`Tjänat ${snap.earnedDays} + överförda ${snap.carriedOverDays} − använd ${snap.usedDays}${hasAdvance ? ` (förskott: ${advance})` : ''}`}
          >
            <Umbrella size={14} className="me-2 text-muted" />
            <Badge
              bg={hasAdvance ? 'warning' : remaining > 0 ? 'success' : 'secondary'}
              text={hasAdvance ? 'dark' : undefined}
              style={{ fontSize: '0.75rem', fontWeight: 500 }}
            >
              {remaining} kvar
              {hasAdvance && <span className="ms-1">(−{advance})</span>}
            </Badge>
          </div>
        );
      },
    },
    {
      Header: 'Kontraktstatus',
      accessor: 'contract_status',
      sortType: 'alphanumeric',
      Cell: ({ row }) => {
        const hasContract = row.original.has_active_contract;
        const contractCount = row.original.contract_count || 0;

        return (
          <div className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
            {hasContract ? (
              <Badge
                bg="success"
                className="d-flex align-items-center gap-1"
                style={{ fontSize: '0.75rem', fontWeight: '500' }}
              >
                <div
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.8)'
                  }}
                />
                Aktivt kontrakt
                {contractCount > 1 && (
                  <span className="ms-1">({contractCount})</span>
                )}
              </Badge>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <Badge
                  bg="warning"
                  text="dark"
                  className="d-flex align-items-center gap-1"
                  style={{ fontSize: '0.75rem', fontWeight: '500' }}
                >
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0,0,0,0.3)'
                    }}
                  />
                  Inget kontrakt
                </Badge>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    history.push(`/${companyId}/employee/${row.original.employee_id}/newcontract`);
                  }}
                  style={{
                    fontSize: '0.75rem',
                    padding: '0.2rem 0.5rem',
                    border: '1px solid var(--bs-primary)',
                    borderRadius: '4px'
                  }}
                  title="Skapa kontrakt"
                >
                  <Plus size={12} className="me-1" />
                  Skapa
                </Button>
              </div>
            )}
          </div>
        );
      }
    },
    {
      Header: "",
      accessor: 'actions',
      disableFilters: true,
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="d-flex gap-2 justify-content-end" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              history.push(`/${companyId}/employee/${row.original.employee_id}`);
            }}
            style={{
              border: 'none',
              background: 'transparent',
              color: 'var(--bs-primary)',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--rgba-primary-1)';
              e.currentTarget.style.color = 'var(--primary-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--bs-primary)';
            }}
            title="Visa detaljer"
          >
            <Eye size={14} />
          </Button>
          <Button
            variant="outline-warning"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              editAction(row.original);
            }}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#f59e0b',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f59e0b10';
              e.currentTarget.style.color = '#d97706';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#f59e0b';
            }}
            title="Redigera"
          >
            <Edit3 size={14} />
          </Button>
          <Button
            variant={actionIcon === Archive ? "outline-danger" : "outline-success"}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              if (!actionInProgress) archiveAction(row.original.employee_id);
            }}
            disabled={actionInProgress}
            style={{
              border: 'none',
              background: 'transparent',
              color: actionIcon === Archive ? '#dc3545' : '#10b981',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!actionInProgress) {
                const bgColor = actionIcon === Archive ? '#dc354510' : '#10b98110';
                const textColor = actionIcon === Archive ? '#b02a37' : '#059669';
                e.currentTarget.style.backgroundColor = bgColor;
                e.currentTarget.style.color = textColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!actionInProgress) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = actionIcon === Archive ? '#dc3545' : '#10b981';
              }
            }}
            title={actionIcon === Archive ? "Arkivera" : "Återställ"}
          >
            {actionIcon === Archive ? <Archive size={14} /> : <RotateCcw size={14} />}
          </Button>
        </div>
      )
    },
  ], [actionIcon, archiveAction, actionInProgress, companyId, history, editAction, vacationSnapshots]);

  const filteredData = useMemo(() => {
    if (!localFilter) return employees;

    return employees.filter(employee => {
      const searchText = localFilter.toLowerCase();
      return (
        employee.name?.toLowerCase().includes(searchText) ||
        employee.email?.toLowerCase().includes(searchText) ||
        employee.ssn?.toLowerCase().includes(searchText)
      );
    });
  }, [employees, localFilter]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page: tablePage,
    gotoPage,
    setPageSize: setTablePageSize,
    state: { pageIndex },
  } = useTable(
    {
      columns: EMPLOYEES_COLUMNS,
      data: filteredData,
      initialState: {
        pageIndex: 0,
        pageSize,
        sortBy: [{ id: 'name', desc: false }]
      },
      disableMultiSort: true,
      disableSortRemove: true,
    },
    useFilters,
    useSortBy,
    usePagination
  );

  // Enhanced pagination controls
  const PaginationControls = () => (
    <div
      style={{
        backgroundColor: '#fff',
        borderTop: '1px solid #f1f5f9',
        padding: '1rem 1.5rem'
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm"
            style={{
              width: 'auto',
              fontSize: '0.875rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px'
            }}
            value={pageSize}
            onChange={e => {
              const newSize = Number(e.target.value);
              setTablePageSize(newSize);
              onPageSizeChange(newSize);
            }}
          >
            {[10, 20, 30, 50, 100].map(size => (
              <option key={size} value={size}>{size} per sida</option>
            ))}
          </select>
          <span style={{ fontSize: '0.875rem', color: 'var(--bs-gray)' }}>
            Visar {tablePage.length} av {filteredData.length} anställda
            {localFilter && ` (filtrerat från ${employees.length})`}
          </span>
        </div>

        {filteredData.length > pageSize && (
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => gotoPage(0)}
              disabled={pageIndex === 0}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              {'<<'}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => gotoPage(pageIndex - 1)}
              disabled={pageIndex === 0}
              style={{ fontSize: '0.75rem' }}
            >
              Föregående
            </Button>
            <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
              Sida {pageIndex + 1} av {Math.max(1, Math.ceil(filteredData.length / pageSize))}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => gotoPage(pageIndex + 1)}
              disabled={pageIndex >= Math.ceil(filteredData.length / pageSize) - 1}
              style={{ fontSize: '0.75rem' }}
            >
              Nästa
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => gotoPage(Math.ceil(filteredData.length / pageSize) - 1)}
              disabled={pageIndex >= Math.ceil(filteredData.length / pageSize) - 1}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              {'>>'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Enhanced search */}
      <div className="p-4 border-bottom" style={{ backgroundColor: '#fafbfc' }}>
        <GlobalFilter filter={localFilter} setFilter={setLocalFilter} />
      </div>

      {filteredData.length === 0 && localFilter && (
        <div className="text-center py-4">
          <div className="text-muted">
            <Search size={24} className="mb-2 opacity-50" />
            <div>Inga anställda matchade din sökning</div>
          </div>
        </div>
      )}

      {filteredData.length === 0 && !localFilter && (
        <div className="text-center py-4">
          <div className="text-muted">Inga anställda att visa</div>
        </div>
      )}

      {filteredData.length > 0 && (
        <>
          <div className="table-responsive">
            <table {...getTableProps()} className="table table-hover mb-0">
              <thead style={{ backgroundColor: '#fff', borderBottom: '2px solid #f1f5f9' }}>
                {headerGroups.map((headerGroup, headerGroupIndex) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={`header-group-${headerGroupIndex}`}>
                    {headerGroup.headers.map((column, columnIndex) => (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        key={`header-${columnIndex}-${column.id}`}
                        className="fw-semibold border-0 py-4 px-4"
                        style={{
                          display: column.hidden ? 'none' : 'table-cell',
                          cursor: column.canSort ? 'pointer' : 'default',
                          userSelect: 'none',
                          fontSize: '0.875rem',
                          letterSpacing: '0.025em',
                          color: '#1e293b',
                          backgroundColor: 'transparent'
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>{column.render('Header')}</span>
                          {column.canSort && (
                            <span className="ms-2">
                              {column.isSorted ? (
                                column.isSortedDesc ?
                                  <i className="fa fa-sort-down" style={{ fontSize: '0.75rem', color: 'var(--bs-primary)' }} />
                                  :
                                  <i className="fa fa-sort-up" style={{ fontSize: '0.75rem', color: 'var(--bs-primary)' }} />
                              ) : (
                                <i className="fa fa-sort" style={{ fontSize: '0.75rem', color: '#cbd5e1', opacity: '0.6' }} />
                              )}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {tablePage.map(row => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      key={row.id}
                      onClick={() => !actionInProgress && history.push(`/${companyId}/employee/${row.original.employee_id}`)}
                      style={{
                        cursor: actionInProgress ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        borderBottom: '1px solid #f1f5f9',
                        opacity: actionInProgress ? 0.6 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!actionInProgress) {
                          e.currentTarget.style.backgroundColor = '#f8fafc';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!actionInProgress) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      {row.cells.map(cell => (
                        <td
                          {...cell.getCellProps()}
                          key={cell.column.id}
                          className="py-4 px-4 align-middle border-0"
                          style={{
                            display: cell.column.hidden ? 'none' : 'table-cell',
                            fontSize: '0.875rem',
                            color: '#374151'
                          }}
                        >
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <PaginationControls />
        </>
      )}
    </>
  );
};

export default EmployeeList;




// import React, { useEffect, useState, useMemo } from 'react';
// import { Link, useParams } from 'react-router-dom';
// import { Dropdown, Tab, Nav, Spinner, Badge, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
// import axios from '../../../../services/AxiosInstance';
// import { getApiUrl } from '../../../../utils/ApiUtils';
// import PageTitle from '../../../layouts/PageTitle';
// import { useTable, useFilters, useGlobalFilter, usePagination, useSortBy } from 'react-table';
// import { GlobalFilter } from './GlobalFilter';
// import { useHistory } from 'react-router-dom';
// import { NoData } from '../../SharedComponents/NoData';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import {
//   faArchive,
//   faUndo,
//   faUsers,
//   faUserSlash,
//   faPlus,
//   faFileExport,
//   faEnvelope,
//   faEdit,
//   faEye,
//   faEllipsisV,
//   faSpinner,
//   faFilter,
//   faSearch
// } from '@fortawesome/free-solid-svg-icons';
// import Swal from 'sweetalert2';
// import EmployeeEditModal from './EmployeeEditModal';

// const EmployeeList = () => {
//   const { companyId } = useParams();
//   const [employees, setEmployees] = useState({ activeEmployees: [], inactiveEmployees: [] });
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [page, setPage] = useState(0);
//   const [pageSize, setPageSize] = useState(10);
//   const [totalPages, setTotalPages] = useState(0);
//   const [actionInProgress, setActionInProgress] = useState(false);
//   const [exportLoading, setExportLoading] = useState(false);
//   const [totalEmployees, setTotalEmployees] = useState(0);
//   const [selectedEmployee, setSelectedEmployee] = useState(null);
//   const [showEditModal, setShowEditModal] = useState(false);

//   const history = useHistory();

//   const fetchEmployees = async () => {
//     setLoading(true);
//     try {
//       const response = await axios.get(
//         getApiUrl(`${companyId}/hr/employees`), {
//         params: {
//           page,
//           pageSize,
//         },
//         withCredentials: true
//       });

//       setEmployees({
//         activeEmployees: response.data.activeEmployees || [],
//         inactiveEmployees: response.data.inactiveEmployees || []
//       });
//       setTotalEmployees(response.data.total || 0);
//       setTotalPages(response.data.totalPages || Math.ceil(response.data.total / pageSize));
//       setError(null);
//     } catch (err) {
//       const errorMessage = err.response?.data?.message || 'Ett fel uppstod vid hämtning av anställda';
//       setError(errorMessage);
//       Swal.fire({
//         icon: 'error',
//         title: 'Fel',
//         text: errorMessage,
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchEmployees();
//   }, [page, pageSize]);

//   // This is the handleExportData function for the EmployeeList component
//   const handleExportData = async (format = 'csv') => {
//     setExportLoading(true);
//     try {
//       const response = await axios.get(
//         getApiUrl(`${companyId}/hr/employees/export`),
//         {
//           params: { format },
//           responseType: 'blob',
//           withCredentials: true
//         }
//       );

//       // Create a blob URL and trigger download
//       const url = window.URL.createObjectURL(new Blob([response.data]));
//       const link = document.createElement('a');
//       link.href = url;
//       link.setAttribute('download', `anstallda-${new Date().toISOString().split('T')[0]}.${format}`);
//       document.body.appendChild(link);
//       link.click();
//       link.remove();

//       // Show success notification
//       Swal.fire({
//         icon: 'success',
//         title: 'Export klar!',
//         text: 'Anställdalistan har exporterats',
//         timer: 1500
//       });
//     } catch (error) {
//       console.error('Export error:', error);
//       let errorMessage = 'Ett fel uppstod vid export av anställdalistan';

//       // Try to get more specific error message if available
//       if (error.response && error.response.data) {
//         try {
//           const blob = error.response.data;
//           const text = await blob.text();
//           const data = JSON.parse(text);
//           if (data && data.message) {
//             errorMessage = data.message;
//           }
//         } catch (e) {
//           // Fallback to default error message
//         }
//       }

//       Swal.fire({
//         icon: 'error',
//         title: 'Export misslyckades',
//         text: errorMessage
//       });
//     } finally {
//       setExportLoading(false);
//     }
//   };

//   const handleArchiveAction = async (employeeId, action) => {
//     const isArchiving = action === 'archive';
//     const result = await Swal.fire({
//       title: isArchiving ? 'Arkivera anställd?' : 'Återställ anställd?',
//       text: isArchiving
//         ? 'Är du säker på att du vill arkivera denna anställd?'
//         : 'Är du säker på att du vill återställa denna anställd?',
//       icon: 'warning',
//       showCancelButton: true,
//       confirmButtonText: isArchiving ? 'Ja, arkivera' : 'Ja, återställ',
//       cancelButtonText: 'Avbryt',
//       confirmButtonColor: '#3085d6',
//       cancelButtonColor: '#d33'
//     });

//     if (result.isConfirmed) {
//       setActionInProgress(true);
//       try {
//         const apiUrl = getApiUrl(`${companyId}/hr/employees/${employeeId}`);
//         const newStatus = isArchiving ? 'archived' : 'active';
//         await axios.patch(apiUrl, { status: newStatus }, { withCredentials: true });
//         await fetchEmployees();
//         Swal.fire({
//           icon: 'success',
//           title: 'Klart!',
//           text: isArchiving
//             ? 'Anställd har arkiverats'
//             : 'Anställd har återställts',
//           timer: 1500
//         });
//       } catch (error) {
//         Swal.fire({
//           icon: 'error',
//           title: 'Fel',
//           text: 'Ett fel uppstod vid uppdatering av anställd status'
//         });
//       } finally {
//         setActionInProgress(false);
//       }
//     }
//   };

//   const handleEditEmployee = (employee) => {
//     console.log('Edit employee:', employee);
//     setSelectedEmployee(employee);
//     setShowEditModal(true);
//   };

//   const handleEditModalClose = () => {
//     setShowEditModal(false);
//     setSelectedEmployee(null);
//   };

//   const handleEmployeeUpdate = (updatedEmployee) => {
//     // Update the employee in both active and inactive lists
//     setEmployees(prev => {
//       const newActiveEmployees = prev.activeEmployees.map(emp =>
//         emp.employee_id === updatedEmployee.employee_id ? updatedEmployee : emp
//       );
//       const newInactiveEmployees = prev.inactiveEmployees.map(emp =>
//         emp.employee_id === updatedEmployee.employee_id ? updatedEmployee : emp
//       );

//       return {
//         activeEmployees: newActiveEmployees,
//         inactiveEmployees: newInactiveEmployees
//       };
//     });

//     // Show success message
//     Swal.fire({
//       icon: 'success',
//       title: 'Sparat!',
//       text: 'Ändringar har sparats.',
//       timer: 1500
//     });
//   };

//   const tabData = useMemo(() => [
//     {
//       name: "Aktiva",
//       data: employees.activeEmployees,
//       icon: faUsers,
//       archiveAction: (employeeId) => handleArchiveAction(employeeId, 'archive'),
//       actionIcon: faArchive,
//       badge: employees.activeEmployees?.length || 0,
//       editAction: handleEditEmployee
//     },
//     {
//       name: "Arkiverade",
//       data: employees.inactiveEmployees,
//       icon: faUserSlash,
//       archiveAction: (employeeId) => handleArchiveAction(employeeId, 'unarchive'),
//       actionIcon: faUndo,
//       badge: employees.inactiveEmployees?.length || 0,
//       editAction: handleEditEmployee
//     }
//   ], [employees]);

//   // First-time loading state
//   if (loading && !employees.activeEmployees.length && !employees.inactiveEmployees.length) {
//     return (
//       <>
//         <PageTitle activeMenu="Anställda" motherMenu="Personal" />
//         <div className="vh-100 d-flex justify-content-center align-items-center">
//           <div className="text-center">
//             <Spinner animation="border" role="status" className="mb-2">
//               <span className="visually-hidden">Laddar...</span>
//             </Spinner>
//             <div>Hämtar anställda...</div>
//           </div>
//         </div>
//       </>
//     );
//   }

//   return (
//     <>
//       <PageTitle activeMenu="Anställda" motherMenu="Personal" />

//       {error && (
//         <Alert variant="danger" className="mb-4">
//           <div className="d-flex align-items-center">
//             <span className="me-3">{error}</span>
//             <button
//               className="btn btn-outline-danger btn-sm ms-auto"
//               onClick={() => fetchEmployees()}
//             >
//               Försök igen
//             </button>
//           </div>
//         </Alert>
//       )}

//       {employees.activeEmployees.length === 0 && employees.inactiveEmployees.length === 0 ? (
//         <>
//           <NoData message="Det finns inga anställda att visa. Skapa en för att kunna se dem här." />
//           <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center p-3">
//             <Link
//               to={`/${companyId}/create-employee`}
//               className="btn btn-primary mt-4"
//             >
//               <FontAwesomeIcon icon={faPlus} className="me-2" />
//               Kom igång och skapa din första anställd
//             </Link>
//           </div>
//         </>
//       ) : (
//         <div className="row">
//           <div className="col-xl-12">
//             <div className="d-flex justify-content-between align-items-center mb-3">
//               <div className="d-flex gap-2 mb-3">
//                 <Link
//                   to={`/${companyId}/create-employee`}
//                   className="btn btn-primary"
//                   disabled={actionInProgress}
//                 >
//                   <FontAwesomeIcon icon={faPlus} className="me-2" />
//                   Lägg till ny anställd
//                 </Link>

//                 <Dropdown>
//                   <Dropdown.Toggle variant="outline-primary" id="export-dropdown" disabled={exportLoading}>
//                     {exportLoading ? (
//                       <>
//                         <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
//                         Exporterar...
//                       </>
//                     ) : (
//                       <>
//                         <FontAwesomeIcon icon={faFileExport} className="me-2" />
//                         Exportera
//                       </>
//                     )}
//                   </Dropdown.Toggle>
//                   <Dropdown.Menu>
//                     <Dropdown.Item onClick={() => handleExportData('csv')}>CSV (.csv)</Dropdown.Item>
//                     {/*<Dropdown.Item onClick={() => handleExportData('pdf')}>PDF (.pdf)</Dropdown.Item>
//                           <Dropdown.Item onClick={() => handleExportData('xlsx')}>Excel (.xlsx)</Dropdown.Item>*/}
//                   </Dropdown.Menu>
//                 </Dropdown>
//               </div>

//               {/*<div className="d-flex align-items-center">
//                     <span className="badge bg-info text-white me-3">
//                       Totalt: {totalEmployees} anställda
//                     </span>
//                   </div>*/}
//             </div>
//             <div className="card">
//               <div className="card-body">


//                 <div className="custom-tab-1">
//                   <Tab.Container defaultActiveKey={tabData[0].name.toLowerCase()}>
//                     <Nav as="ul" className="nav-tabs">
//                       {tabData.map((data, i) => (
//                         <Nav.Item as="li" key={i}>
//                           <Nav.Link eventKey={data.name.toLowerCase()} className="d-flex align-items-center">
//                             <FontAwesomeIcon icon={data.icon} className="me-2" />
//                             {data.name}
//                             <Badge bg="primary" className="ms-2">
//                               {data.badge}
//                             </Badge>
//                           </Nav.Link>
//                         </Nav.Item>
//                       ))}
//                     </Nav>
//                     <Tab.Content className="pt-4">
//                       {loading && (
//                         <div className="text-center py-4">
//                           <Spinner animation="border" size="sm" />
//                           <span className="ms-2">Uppdaterar...</span>
//                         </div>
//                       )}
//                       {!loading && tabData.map((data, i) => (
//                         <Tab.Pane eventKey={data.name.toLowerCase()} key={i}>
//                           <EmployeeTable
//                             employees={data.data}
//                             archiveAction={data.archiveAction}
//                             actionIcon={data.actionIcon}
//                             editAction={data.editAction}
//                             page={page}
//                             pageSize={pageSize}
//                             totalPages={totalPages}
//                             onPageChange={setPage}
//                             onPageSizeChange={setPageSize}
//                             actionInProgress={actionInProgress}
//                           />
//                         </Tab.Pane>
//                       ))}
//                     </Tab.Content>
//                   </Tab.Container>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Employee Edit Modal */}
//       {selectedEmployee && (
//         <EmployeeEditModal
//           show={showEditModal}
//           handleClose={handleEditModalClose}
//           employee={selectedEmployee}
//           companyId={companyId}
//           onSave={handleEmployeeUpdate}
//         />
//       )}
//     </>
//   );
// };

// const EmployeeTable = ({
//   employees,
//   archiveAction,
//   actionIcon,
//   editAction,
//   page,
//   pageSize,
//   totalPages,
//   onPageChange,
//   onPageSizeChange,
//   actionInProgress
// }) => {
//   const { companyId } = useParams();
//   const history = useHistory();
//   const [localFilter, setLocalFilter] = useState('');

//   const EMPLOYEES_COLUMNS = useMemo(() => [
//     {
//       Header: 'Id',
//       accessor: 'employee_id',
//       show: false,
//       hidden: true
//     },
//     {
//       Header: 'Namn',
//       accessor: 'name',
//       sortType: 'alphanumeric'
//     },
//     {
//       Header: 'E-post',
//       accessor: 'email',
//       sortType: 'alphanumeric',
//       Cell: ({ value }) => (
//         <OverlayTrigger
//           placement="top"
//           overlay={<Tooltip>Skicka e-post</Tooltip>}
//         >
//           <a
//             href={`mailto:${value}`}
//             onClick={(e) => e.stopPropagation()}
//             className="text-primary"
//           >
//             {value}
//           </a>
//         </OverlayTrigger>
//       )
//     },
//     {
//       Header: 'Personnummer',
//       accessor: 'ssn',
//       sortType: 'alphanumeric'
//     },
//     {
//       Header: "",
//       accessor: 'actions',
//       disableFilters: true,
//       disableSortBy: true,
//       Cell: ({ row }) => (
//         <div className="d-flex justify-content-end gap-2" onClick={(e) => e.stopPropagation()}>
//           <OverlayTrigger
//             placement="top"
//             overlay={<Tooltip>Visa detaljer</Tooltip>}
//           >
//             <FontAwesomeIcon
//               icon={faEye}
//               className="text-primary"
//               style={{ cursor: 'pointer' }}
//               onClick={() => history.push(`/${companyId}/employee/${row.original.employee_id}`)}
//             />
//           </OverlayTrigger>

//           <OverlayTrigger
//             placement="top"
//             overlay={<Tooltip>Redigera</Tooltip>}
//             className="color-primary"
//           >
//             <FontAwesomeIcon
//               icon={faEdit}
//               className="text-warning"
//               style={{ cursor: 'pointer' }}
//               onClick={() => editAction(row.original)}
//             />
//           </OverlayTrigger>

//           <OverlayTrigger
//             placement="top"
//             overlay={<Tooltip>{actionIcon === faArchive ? 'Arkivera' : 'Återställ'}</Tooltip>}
//           >
//             <FontAwesomeIcon
//               icon={actionIcon}
//               className={actionIcon === faArchive ? 'text-danger' : 'text-success'}
//               onClick={() => !actionInProgress && archiveAction(row.original.employee_id)}
//               style={{
//                 cursor: actionInProgress ? 'not-allowed' : 'pointer',
//                 opacity: actionInProgress ? 0.5 : 1
//               }}
//             />
//           </OverlayTrigger>
//         </div>
//       )
//     },
//   ], [actionIcon, archiveAction, actionInProgress, companyId, history, editAction]);

//   // Client-side filtering function
//   const filteredData = useMemo(() => {
//     if (!localFilter) return employees;

//     return employees.filter(employee => {
//       const searchText = localFilter.toLowerCase();
//       return (
//         employee.name?.toLowerCase().includes(searchText) ||
//         employee.email?.toLowerCase().includes(searchText) ||
//         employee.ssn?.toLowerCase().includes(searchText)
//       );
//     });
//   }, [employees, localFilter]);

//   // Table configuration
//   const {
//     getTableProps,
//     getTableBodyProps,
//     headerGroups,
//     prepareRow,
//     page: tablePage,
//     gotoPage,
//     setPageSize: setTablePageSize,
//     state: { pageIndex },
//   } = useTable(
//     {
//       columns: EMPLOYEES_COLUMNS,
//       data: filteredData,
//       initialState: {
//         pageIndex: 0,
//         pageSize,
//         sortBy: [{ id: 'name', desc: false }]
//       },
//       disableMultiSort: true,
//       disableSortRemove: true,
//     },
//     useFilters,
//     useSortBy,
//     usePagination
//   );

//   return (
//     <>
//       <div className="d-flex justify-content-between align-items-center mb-3">
//         <div className="flex-grow-1 me-3 position-relative">
//           <div className="input-group">
//             <span className="input-group-text bg-light">
//               <FontAwesomeIcon icon={faSearch} />
//             </span>
//             <input
//               type="text"
//               className="form-control"
//               placeholder="Sök på namn, e-post eller personnummer..."
//               value={localFilter}
//               onChange={e => setLocalFilter(e.target.value)}
//             />
//             {localFilter && (
//               <button
//                 className="btn btn-outline-secondary"
//                 type="button"
//                 onClick={() => setLocalFilter('')}
//               >
//                 &times;
//               </button>
//             )}
//           </div>
//         </div>
//         <div className="d-flex align-items-center gap-3">
//           <span className="text-muted">Rader per sida:</span>
//           <select
//             className="form-select form-select-sm"
//             style={{ width: 'auto', minWidth: '100px' }}
//             value={pageSize}
//             onChange={e => {
//               const newSize = Number(e.target.value);
//               setTablePageSize(newSize);
//               onPageSizeChange(newSize);
//             }}
//           >
//             {[10, 20, 30, 50, 100].map(size => (
//               <option key={size} value={size}>
//                 {size}
//               </option>
//             ))}
//           </select>
//         </div>
//       </div>

//       {filteredData.length === 0 && (
//         <div className="alert alert-info">
//           <FontAwesomeIcon icon={faFilter} className="me-2" />
//           Inga anställda matchade din sökning
//         </div>
//       )}

//       {filteredData.length > 0 && (
//         <div className="table-responsive">
//           <table {...getTableProps()} className="table table-hover">
//             <thead>
//               {headerGroups.map(headerGroup => (
//                 <tr {...headerGroup.getHeaderGroupProps()}>
//                   {headerGroup.headers.map(column => (
//                     <th
//                       {...column.getHeaderProps(column.getSortByToggleProps())}
//                       style={{
//                         display: column.hidden ? 'none' : 'table-cell',
//                         cursor: column.canSort ? 'pointer' : 'default'
//                       }}
//                       className="align-middle"
//                     >
//                       <div className="d-flex align-items-center">
//                         {column.render('Header')}
//                         {column.canSort && (
//                           <span className="ms-2">
//                             {column.isSorted ? (
//                               column.isSortedDesc ? '↓' : '↑'
//                             ) : ''}
//                           </span>
//                         )}
//                       </div>
//                     </th>
//                   ))}
//                 </tr>
//               ))}
//             </thead>
//             <tbody {...getTableBodyProps()}>
//               {tablePage.map(row => {
//                 prepareRow(row);
//                 return (
//                   <tr
//                     {...row.getRowProps()}
//                     className={`align-middle ${actionInProgress ? 'disabled' : ''}`}
//                     onClick={() => !actionInProgress && history.push(`/${companyId}/employee/${row.original.employee_id}`)}
//                     style={{ cursor: actionInProgress ? 'not-allowed' : 'pointer' }}
//                   >
//                     {row.cells.map(cell => (
//                       <td
//                         {...cell.getCellProps()}
//                         style={{
//                           display: cell.column.hidden ? 'none' : 'table-cell'
//                         }}
//                         className="align-middle"
//                       >
//                         {cell.render('Cell')}
//                       </td>
//                     ))}
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         </div>
//       )}

//       {/* Pagination controls */}
//       <div className="d-flex justify-content-between align-items-center mt-3">
//         <div className="text-muted">
//           {filteredData.length > 0 ? (
//             <>
//               Visar {pageIndex * pageSize + 1} till {Math.min((pageIndex + 1) * pageSize, filteredData.length)} av {filteredData.length} anställda
//               {localFilter && ` (filtrerat från ${employees.length})`}
//             </>
//           ) : (
//             'Inga anställda att visa'
//           )}
//         </div>

//         {filteredData.length > pageSize && (
//           <div className="btn-group">
//             <button
//               className="btn btn-outline-primary"
//               onClick={() => gotoPage(0)}
//               disabled={pageIndex === 0}
//               aria-label="Första sidan"
//             >
//               {'<<'}
//             </button>
//             <button
//               className="btn btn-outline-primary"
//               onClick={() => gotoPage(pageIndex - 1)}
//               disabled={pageIndex === 0}
//               aria-label="Föregående sida"
//             >
//               {'<'}
//             </button>
//             {/* Current page indicator */}
//             <button
//               className="btn btn-outline-primary"
//               disabled
//             >
//               {pageIndex + 1} / {Math.max(1, Math.ceil(filteredData.length / pageSize))}
//             </button>
//             <button
//               className="btn btn-outline-primary"
//               onClick={() => gotoPage(pageIndex + 1)}
//               disabled={pageIndex >= Math.ceil(filteredData.length / pageSize) - 1}
//               aria-label="Nästa sida"
//             >
//               {'>'}
//             </button>
//             <button
//               className="btn btn-outline-primary"
//               onClick={() => gotoPage(Math.ceil(filteredData.length / pageSize) - 1)}
//               disabled={pageIndex >= Math.ceil(filteredData.length / pageSize) - 1}
//               aria-label="Sista sidan"
//             >
//               {'>>'}
//             </button>
//           </div>
//         )}
//       </div>
//     </>
//   );
// };

// export default EmployeeList;







// // import React, { useEffect, useState, useMemo } from 'react';
// // import { Link, useParams } from 'react-router-dom';
// // import { Dropdown, Tab, Nav, Spinner } from 'react-bootstrap';
// // import axios from '../../../../services/AxiosInstance';
// // import { getApiUrl } from '../../../../utils/ApiUtils';
// // import PageTitle from '../../../layouts/PageTitle';
// // import { useTable, useFilters, useGlobalFilter, usePagination } from 'react-table';
// // import { GlobalFilter } from './GlobalFilter';
// // import { useHistory } from 'react-router-dom';
// // import { NoData } from '../../SharedComponents/NoData';
// // import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// // import { faArchive, faUndo, faUsers, faUserSlash, faPlus } from '@fortawesome/free-solid-svg-icons';
// // import Swal from 'sweetalert2';

// // const EmployeeList = () => {
// //   const { companyId } = useParams();
// //   const [employees, setEmployees] = useState({ activeEmployees: [], inactiveEmployees: [] });
// //   const [loading, setLoading] = useState(true);
// //   const [error, setError] = useState(null);
// //   const [page, setPage] = useState(0);
// //   const [pageSize, setPageSize] = useState(10);
// //   const [totalPages, setTotalPages] = useState(0);
// //   const [actionInProgress, setActionInProgress] = useState(false);

// //   const history = useHistory();

// //   const fetchEmployees = async (pageNum = page) => {
// //     setLoading(true);
// //     try {
// //       const response = await axios.get(
// //         getApiUrl(`${companyId}/hr/employees`), {
// //         params: {
// //           page: pageNum,
// //           pageSize,
// //         },
// //         withCredentials: true
// //       }
// //       );
// //       setEmployees(response.data);
// //       setTotalPages(Math.ceil(response.data.total / pageSize));
// //       setError(null);
// //     } catch (err) {
// //       setError(err.response?.data?.message || 'Ett fel uppstod vid hämtning av anställda');
// //       Swal.fire({
// //         icon: 'error',
// //         title: 'Fel',
// //         text: 'Ett fel uppstod vid hämtning av anställda',
// //       });
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   useEffect(() => {
// //     fetchEmployees();
// //   }, [page, pageSize]);

// //   const handleArchiveAction = async (employeeId, action) => {
// //     const isArchiving = action === 'archive';
// //     const result = await Swal.fire({
// //       title: isArchiving ? 'Arkivera anställd?' : 'Återställ anställd?',
// //       text: isArchiving
// //         ? 'Är du säker på att du vill arkivera denna anställd?'
// //         : 'Är du säker på att du vill återställa denna anställd?',
// //       icon: 'warning',
// //       showCancelButton: true,
// //       confirmButtonText: isArchiving ? 'Ja, arkivera' : 'Ja, återställ',
// //       cancelButtonText: 'Avbryt',
// //       confirmButtonColor: '#3085d6',
// //       cancelButtonColor: '#d33'
// //     });

// //     if (result.isConfirmed) {
// //       setActionInProgress(true);
// //       try {
// //         const apiUrl = getApiUrl(`${companyId}/hr/employees/${employeeId}`);
// //         const newStatus = isArchiving ? 'archived' : 'active';
// //         await axios.patch(apiUrl, { status: newStatus }, { withCredentials: true });
// //         await fetchEmployees();
// //         Swal.fire({
// //           icon: 'success',
// //           title: 'Klart!',
// //           text: isArchiving
// //             ? 'Anställd har arkiverats'
// //             : 'Anställd har återställts',
// //           timer: 1500
// //         });
// //       } catch (error) {
// //         Swal.fire({
// //           icon: 'error',
// //           title: 'Fel',
// //           text: 'Ett fel uppstod vid uppdatering av anställd status'
// //         });
// //       } finally {
// //         setActionInProgress(false);
// //       }
// //     }
// //   };

// //   const tabData = useMemo(() => [
// //     {
// //       name: "Aktiva",
// //       data: employees.activeEmployees,
// //       icon: faUsers,
// //       archiveAction: (employeeId) => handleArchiveAction(employeeId, 'archive'),
// //       actionIcon: faArchive
// //     },
// //     {
// //       name: "Inaktiva",
// //       data: employees.inactiveEmployees,
// //       icon: faUserSlash,
// //       archiveAction: (employeeId) => handleArchiveAction(employeeId, 'unarchive'),
// //       actionIcon: faUndo
// //     }
// //   ], [employees]);

// //   if (loading && !employees.activeEmployees.length && !employees.inactiveEmployees.length) {
// //     return (
// //       <>
// //         <PageTitle activeMenu="Anställda" motherMenu="Personal" />
// //         <div className="vh-100 d-flex justify-content-center align-items-center">
// //           <Spinner animation="border" role="status" className="me-2">
// //             <span className="visually-hidden">Loading...</span>
// //           </Spinner>
// //           <div>Loading...</div>
// //         </div>
// //       </>
// //     );
// //   }

// //   return (
// //     <>
// //       <PageTitle activeMenu="Anställda" motherMenu="Personal" />

// //       {employees.activeEmployees.length === 0 && employees.inactiveEmployees.length === 0 ? (
// //         <>
// //           <NoData message="Det finns inga anställda att visa. Skapa en för att kunna se dem här." />
// //           <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center p-3">
// //             <Link
// //               to={`/${companyId}/create-employee`}
// //               className="btn btn-primary mt-4"
// //             >
// //               Kom igång och skapa din första anställd
// //             </Link>
// //           </div>
// //         </>
// //       ) : (
// //         <div className="row">
// //           <div className="col-xl-12">
// //             <div className="card">
// //               <div className="card-body">
// //                 <div className="d-flex justify-content-end mb-3">
// //                   <Link
// //                     to={`/${companyId}/create-employee`}
// //                     className="btn btn-primary"
// //                     disabled={actionInProgress}
// //                   >
// //                     <FontAwesomeIcon icon={faPlus} className="me-2" />
// //                     Lägg till ny anställd
// //                   </Link>
// //                 </div>
// //                 <div className="custom-tab-1">
// //                   <Tab.Container defaultActiveKey={tabData[0].name.toLowerCase()}>
// //                     <Nav as="ul" className="nav-tabs">
// //                       {tabData.map((data, i) => (
// //                         <Nav.Item as="li" key={i}>
// //                           <Nav.Link eventKey={data.name.toLowerCase()}>
// //                             <FontAwesomeIcon icon={data.icon} className="me-2" />
// //                             {data.name}
// //                           </Nav.Link>
// //                         </Nav.Item>
// //                       ))}
// //                     </Nav>
// //                     <Tab.Content className="pt-4">
// //                       {tabData.map((data, i) => (
// //                         <Tab.Pane eventKey={data.name.toLowerCase()} key={i}>
// //                           <EmployeeTable
// //                             employees={data.data}
// //                             archiveAction={data.archiveAction}
// //                             actionIcon={data.actionIcon}
// //                             page={page}
// //                             pageSize={pageSize}
// //                             totalPages={totalPages}
// //                             onPageChange={setPage}
// //                             onPageSizeChange={setPageSize}
// //                             actionInProgress={actionInProgress}
// //                           />
// //                         </Tab.Pane>
// //                       ))}
// //                     </Tab.Content>
// //                   </Tab.Container>
// //                 </div>
// //               </div>
// //             </div>
// //           </div>
// //         </div>
// //       )}
// //     </>
// //   );
// // };

// // const EmployeeTable = ({
// //   employees,
// //   archiveAction,
// //   actionIcon,
// //   page,
// //   pageSize,
// //   totalPages,
// //   onPageChange,
// //   onPageSizeChange,
// //   actionInProgress
// // }) => {
// //   const { companyId } = useParams();
// //   const history = useHistory();

// //   const EMPLOYEES_COLUMNS = useMemo(() => [
// //     { Header: 'Id', accessor: 'employee_id', show: false, hidden: true },
// //     { Header: 'Namn', Footer: 'Namn', accessor: 'name' },
// //     { Header: 'E-post', Footer: 'Email', accessor: 'email' },
// //     { Header: 'Personnummer', Footer: 'Personnummer', accessor: 'ssn' },
// //     {
// //       Header: "",
// //       Footer: "",
// //       accessor: 'actions',
// //       hidden: false,
// //       disableFilters: true,
// //       Cell: ({ row }) => (
// //         <div
// //           className="d-flex justify-content-between"
// //           onClick={(e) => e.stopPropagation()}
// //         >
// //           <FontAwesomeIcon
// //             icon={actionIcon}
// //             onClick={() => !actionInProgress && archiveAction(row.original.employee_id)}
// //             style={{
// //               cursor: actionInProgress ? 'not-allowed' : 'pointer',
// //               opacity: actionInProgress ? 0.5 : 1
// //             }}
// //           />
// //         </div>
// //       )
// //     },
// //   ], [actionIcon, archiveAction, actionInProgress]);

// //   const {
// //     getTableProps,
// //     getTableBodyProps,
// //     headerGroups,
// //     prepareRow,
// //     state,
// //     page: tablePage,
// //     setGlobalFilter,
// //     gotoPage,
// //     setPageSize: setTablePageSize,
// //   } = useTable(
// //     {
// //       columns: EMPLOYEES_COLUMNS,
// //       data: employees,
// //       initialState: {
// //         pageIndex: page,
// //         pageSize: pageSize
// //       },
// //       manualPagination: true,
// //       pageCount: totalPages,
// //     },
// //     useFilters,
// //     useGlobalFilter,
// //     usePagination
// //   );

// //   const { globalFilter } = state;

// //   const handleRowClick = (employeeId) => {
// //     if (!actionInProgress) {
// //       history.push(`/${companyId}/employee/${employeeId}`);
// //     }
// //   };

// //   return (
// //     <>
// //       <div className="d-flex justify-content-between align-items-center mb-3">
// //         <div className="flex-grow-1 me-3">
// //           <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
// //         </div>
// //         <select
// //           className="form-select"
// //           style={{ width: 'auto', minWidth: '120px' }}
// //           value={pageSize}
// //           onChange={e => {
// //             setTablePageSize(Number(e.target.value));
// //             onPageSizeChange(Number(e.target.value));
// //           }}
// //         >
// //           {[10, 20, 30, 40, 50].map(size => (
// //             <option key={size} value={size}>
// //               Visa {size}
// //             </option>
// //           ))}
// //         </select>
// //       </div>

// //       <div className="table-responsive">
// //         <table {...getTableProps()} className="table dataTable table-hover display">
// //           <thead>
// //             {headerGroups.map(headerGroup => (
// //               <tr {...headerGroup.getHeaderGroupProps()}>
// //                 {headerGroup.headers.map(column => (
// //                   <th
// //                     {...column.getHeaderProps()}
// //                     style={{ display: column.hidden ? 'none' : 'table-cell' }}
// //                   >
// //                     {column.render('Header')}
// //                   </th>
// //                 ))}
// //               </tr>
// //             ))}
// //           </thead>
// //           <tbody {...getTableBodyProps()}>
// //             {tablePage.map(row => {
// //               prepareRow(row);
// //               return (
// //                 <tr
// //                   className={`clickable-row ${actionInProgress ? 'disabled' : ''}`}
// //                   onClick={() => handleRowClick(row.original.employee_id)}
// //                   {...row.getRowProps()}
// //                   style={{ cursor: actionInProgress ? 'not-allowed' : 'pointer' }}
// //                 >
// //                   {row.cells.map(cell => (
// //                     <td
// //                       {...cell.getCellProps()}
// //                       style={{ display: cell.column.hidden ? 'none' : 'table-cell' }}
// //                     >
// //                       {cell.render('Cell')}
// //                     </td>
// //                   ))}
// //                 </tr>
// //               );
// //             })}
// //           </tbody>
// //         </table>
// //       </div>

// //       {employees.length > pageSize && (
// //         <div className="d-flex justify-content-between align-items-center mt-3">
// //           <div>
// //             Visar {page * pageSize + 1} till {Math.min((page + 1) * pageSize, employees.length)} av {employees.length} anställda
// //           </div>
// //           <div className="btn-group">
// //             <button
// //               className="btn btn-primary"
// //               onClick={() => {
// //                 gotoPage(0);
// //                 onPageChange(0);
// //               }}
// //               disabled={page === 0 || employees.length <= pageSize}
// //             >
// //               {'<<'}
// //             </button>
// //             <button
// //               className="btn btn-primary"
// //               onClick={() => {
// //                 const newPage = page - 1;
// //                 gotoPage(newPage);
// //                 onPageChange(newPage);
// //               }}
// //               disabled={page === 0 || employees.length <= pageSize}
// //             >
// //               {'<'}
// //             </button>
// //             <button
// //               className="btn btn-primary"
// //               onClick={() => {
// //                 const newPage = page + 1;
// //                 gotoPage(newPage);
// //                 onPageChange(newPage);
// //               }}
// //               disabled={page >= Math.ceil(employees.length / pageSize) - 1 || employees.length <= pageSize}
// //             >
// //               {'>'}
// //             </button>
// //             <button
// //               className="btn btn-primary"
// //               onClick={() => {
// //                 const newPage = Math.ceil(employees.length / pageSize) - 1;
// //                 gotoPage(newPage);
// //                 onPageChange(newPage);
// //               }}
// //               disabled={page >= Math.ceil(employees.length / pageSize) - 1 || employees.length <= pageSize}
// //             >
// //               {'>>'}
// //             </button>
// //           </div>
// //         </div>
// //       )}
// //     </>
// //   );
// // };

// // export default EmployeeList;